import type { CreateEstimation } from './estimationScheme';

export const DependencyType = {
  Successor: 'SUCCESSOR',
  Predecessor: 'PREDECESSOR',
} as const;

export type DependencyType = (typeof DependencyType)[keyof typeof DependencyType];

export const SchedulingType = {
  ACTUALS_ONLY: 'ACTUALS_ONLY',
  PLANNED_AND_ACTUALS: 'PLANNED_AND_ACTUALS',
} as const;

export type SchedulingType = (typeof SchedulingType)[keyof typeof SchedulingType];

export interface ItemDependency {
  dependencyType: DependencyType;
  dependencyItemId: number;
}

export type ItemEstimation = CreateEstimation & { schemeId: number };

export interface ItemDescendantsDetails {
  itemId: number;
  hasChildren: boolean;
}

export interface Item {
  id: number;
  itemTypeId: number;
  itemStatusId: number;
  name: string;
  description: string | null;
  parentId: number | null;
  schedulingType: SchedulingType;
  teamId: number | null;
  timeSpentSeconds: number | null;
  targetStartDateTime: string | null;
  targetFinishDateTime: string | null;
  plannedStartDateTime: string | null;
  plannedFinishDateTime: string | null;
  doneDateTime: string | null;
  externalKey?: string | null;
  externalLink?: string | null;
  loopId: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  customFields: ItemCustomFieldValues;
  planId?: number;
  estimationId?: number | null;
  priority: string;
  dependencies: ItemDependency[];
}

export type CustomFieldValueDto = {
  value: string | number | boolean | null;
  description?: string;
};

export type CustomFieldValuesEntry = CustomFieldValueDto | CustomFieldValueDto[] | null | undefined;

export const isCustomFieldValueDto = (v: unknown): v is CustomFieldValueDto =>
  typeof v === 'object' && v !== null && !Array.isArray(v) && 'value' in v;

export const unwrapSingleDto = (entry: CustomFieldValuesEntry): CustomFieldValueDto['value'] =>
  entry == null || Array.isArray(entry) ? null : entry.value;

export const unwrapMultiDto = (entry: CustomFieldValuesEntry): CustomFieldValueDto['value'][] =>
  Array.isArray(entry) ? entry.map((dto) => dto.value) : [];

export const wrapAsDtos = <T extends string | number | boolean>(
  values: T[],
): CustomFieldValueDto[] => values.map((value) => ({ value }));

export type ItemV2 = Omit<Item, 'customFields'> & {
  customFieldValues?: {
    [key: string]: CustomFieldValuesEntry;
  };
};

export type ItemCustomFieldValues = {
  [key: string]: ItemCustomFieldValue;
};

export type ItemPicklistValue = { PICKLIST: number };
export type ItemMultiPicklistValue = { id: number; MULTI_PICKLIST: number };
export type ItemItemValue = { ITEM: number };
export type ItemMultiItemValue = { id: number; MULTI_ITEM: number };
export type ItemUserValue = { USER: string };
export type ItemMultiUserValue = { id: number; MULTI_USER: string };
export type ItemMultiCustomerValue = { id: number; MULTI_CUSTOMER: number };
export type ItemLinkValue = { LINK: string; description?: string };
export type ItemMultiLinkValue = { id: number; MULTI_LINK: string; description?: string };
export type ItemTextValue = { TEXT: string };
export type ItemMultiLineTextValue = { MULTILINE_TEXT: string };
export type ItemNoteValue = { NOTE: string };
export type ItemNumericValue = { NUMERIC: number }; // numeric
export type ItemCurrencyValue = { CURRENCY: number }; // currency
export type ItemMultiTextValue = { id: number; MULTI_TEXT: string };
export type ItemCheckboxValue = { CHECKBOX: boolean };
export type ItemDateValue = { DATE: string };

export type ItemCustomFieldValue =
  | ItemPicklistValue
  | ItemMultiPicklistValue[]
  | ItemItemValue
  | ItemMultiItemValue[]
  | ItemUserValue
  | ItemMultiUserValue
  | ItemTextValue
  | ItemMultiLineTextValue
  | ItemNoteValue
  | ItemLinkValue
  | ItemMultiLinkValue[]
  | ItemNumericValue
  | ItemMultiTextValue[]
  | ItemMultiUserValue[]
  | ItemMultiCustomerValue[]
  | ItemCurrencyValue
  | ItemCheckboxValue
  | ItemDateValue;

export type ItemDataSet = Record<number, DataSetItem>;

interface DataSetItem {
  parentId: number | null;
  level: number;
}

export interface ItemTreeSetEntry {
  id: number;
  parentId: number | null;
  matched: boolean;
}

export interface ItemTreeSet {
  recordCount: number | null;
  data: ItemTreeSetEntry[];
}

export interface ItemAncestor {
  id: number;
  depth: number;
  name?: string;
}

export interface PaginatedItems {
  recordLimit: number;
  recordCount: number;
  cursor?: string;
  data: readonly Item[];
}

export type ItemRequestModelField = 'DEPENDENCIES' | 'CUSTOM_FIELDS' | 'INTEGRATIONS';

export interface ItemFieldDto {
  name: string;
  projectable: boolean;
  sortable: boolean;
}

export interface ItemPatchRequest {
  itemTypeId?: number | null;
  name?: string | null;
  parentId?: number | null;
  schedulingType?: SchedulingType | null;
  description?: string | null;
  itemStatusId?: number | null;
  teamId?: number | null;
  estimationId?: number | null;
  targetStartDateTime?: string | null;
  targetFinishDateTime?: string | null;
  plannedStartDateTime?: string | null;
  plannedFinishDateTime?: string | null;
  doneDateTime?: string | null;
  timeSpentSeconds?: number | null;
  planId?: number | null;
  relativePriority?: { id: number; type: 'before' | 'after' };
}

export interface CreateItemRequest {
  itemTypeId: number;
  name: string;
  parentId?: number | null;
  schedulingType: SchedulingType;
  description?: string | null;
  itemStatusId: number;
  estimationId?: number | null;
  teamId?: number | null;
  targetStartDateTime?: string | null;
  targetFinishDateTime?: string | null;
  loopId: number;
}

// Item Aggregations
export type ItemAggregationFunction = 'SUM' | 'MIN' | 'MAX' | 'AVG';
export type ItemAggregationField =
  | 'TIME_SPENT'
  | 'LOW_EFFORT'
  | 'HIGH_EFFORT'
  | 'EXPECTED_FINISH_DATE'
  | 'PROGRESS_PERCENTAGE';
export type ItemAggregationDirection =
  | 'DESCENDANT'
  | 'ANCESTOR'
  | 'SELF_OR_DESCENDANT'
  | 'SELF_AND_DESCENDANT';

export interface ItemAggregationSpec {
  function: ItemAggregationFunction;
  field: ItemAggregationField;
  direction: ItemAggregationDirection;
}

export interface ItemAggregationRequest {
  ids: number[];
  aggregations: ItemAggregationSpec[];
}

export interface ItemAggregationResult {
  function: ItemAggregationFunction;
  field: ItemAggregationField;
  direction: ItemAggregationDirection;
  result: number | string | null;
}

export interface ItemAggregationsResults {
  id: number;
  aggregations: ItemAggregationResult[];
}

export interface ItemAggregationResponse {
  data: ItemAggregationsResults[];
}

export interface BulkFetchV2Response {
  itemsById: Record<string, Partial<ItemV2>>;
}

export interface TypedFieldValue {
  value: string | number | boolean | null;
  id?: number | null;
  description?: string | null;
}

export interface CustomFieldPatch {
  fieldId: number;
  fieldValue: TypedFieldValue[];
}

export interface BulkPartialPatchRequest {
  itemIds: number[];
  fields?: ItemPatchRequest | null;
  customFields?: CustomFieldPatch[] | null;
}

export interface BulkPatchItemError {
  itemId: number;
  errorCode: 'NOT_FOUND' | 'VALIDATION' | 'CONFLICT' | 'BATCH_FAILURE';
  message: string;
}

export interface BulkPartialPatchResponse {
  failed: BulkPatchItemError[];
}
